'use client';

import React from 'react';
import { motion } from 'framer-motion';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographyH4, TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';
import type { ProjectDesignDetail } from '../../data/project-data';

const SECTION_ID = 'project-design';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  },
};

const itemMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

interface ProjectDesignSectionProperties {
  designDetails: ProjectDesignDetail[];
  projectName: string;
}

function ProjectDesignSection({
  designDetails,
  projectName,
}: ProjectDesignSectionProperties) {
  return (
    <SectionBase id={SECTION_ID} background="bg-background">
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        <SectionHeader
          subtitle="DESIGN & FINISHES"
          title={`Craftsmanship Behind ${projectName}`}
          description="A closer look at the materials, finishes, and design choices that bring this home to life."
        />

        <div className={cls`flex flex-col gap-8 w-full max-w-3xl mx-auto`}>
          {designDetails.map((detail, index) => (
            <motion.div
              key={detail.title}
              className={cls`flex flex-col gap-3 p-6 rounded-lg border border-border`}
              {...itemMotionProps}
            >
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  {index + 1}
                </span>
                <TypographyH4 className="text-foreground">
                  {detail.title}
                </TypographyH4>
              </div>
              <TypographySmall className="text-muted-foreground leading-relaxed pl-11">
                {detail.description}
              </TypographySmall>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </SectionBase>
  );
}

export default ProjectDesignSection;
